import { EventObject, MetaObject } from 'xstate';
import {
  LogicContext,
  GameLaunchEvent,
  ControllersData,
  SegmentsEvent,
} from './types';
import config from '../../config';
import { isIframeWrapperEnabled } from './utils';
import { ALL_CONTROLLERS, controllersEnum } from '../controllers';
import { PortalVerticalGameTilePropTypes } from '../../types/gameTypes';

const currentlyOnRecentlyPlayedPage = (context: LogicContext): boolean => {
  return (
    !!context.controllersData[controllersEnum.RECENTLY_PLAYED] &&
    context.controllersData[controllersEnum.RECENTLY_PLAYED]!.components.some(
      ([id, entry]) => entry.properties.type === 'Grid'
    )
  );
};

const requiresScrollRestore = (context: LogicContext): boolean =>
  context.restoreScroll;

const isLargeScreenWithHeader = (context: LogicContext): boolean => {
  return (
    context.currentScreenSize === 'wide' &&
    !isIframeWrapperEnabled() &&
    context.deviceInfo.deviceType === 'desktop'
  );
};

// Vegas/visible mode, Xsell/hidden mode
const isHeaderInVisibleMode = (): boolean => !isIframeWrapperEnabled();

const isRoutingEnabled = (): boolean => config.history.enabled;

const isRoutingNotEnabled = (): boolean => {
  return !isRoutingEnabled();
};

const isAuthTokenExpired = (context: LogicContext, event: any): boolean => {
  return event.data?.error && event.data.error === 'invalid_grant';
};

const isExternalAuthTokenVariant = (): boolean =>
  config.features.session.authToken.variant === 'external';

const isInternalAuthTokenVariant = (): boolean =>
  config.features.session.authToken.variant === 'internal';

const isSearchWithoutGameDetailsPage = (context: LogicContext) =>
  context.trackingData.sidebar?.SiteSubSection === 'Search';

const isSessionNotResolved = (context: LogicContext): boolean =>
  !context.sessionResolved;

const isSidebarVisible = (context: LogicContext): boolean =>
  context.isSidebarInLayout;

const isModalVisibleOnBigScreen = (context: LogicContext): boolean =>
  context.isSidebarInLayout && context.currentScreenSize !== 'small';

const isGameDetailsPage = (context: LogicContext): boolean => {
  return !!context.controllersData[controllersEnum.GAME_DETAILS];
};

const delayedTrackingNotInstalled = (context: LogicContext): boolean =>
  !context.delayedTrackingAvailable;

const isNotAllowedToLaunchGame = (context: LogicContext): boolean =>
  !isIframeWrapperEnabled() ? !context.user.loggedIn : false;

const isGameTriggeredByUserInteraction = (context: LogicContext) =>
  context.gameToLaunch?.triggeredByUser;

const isNotAwaitingLogin = (context: LogicContext): boolean =>
  !context.loginModalState.isAwaitingLogin;

// TODO change condition for tracking
const isGameLaunchedFromSearchCategory = (
  context: LogicContext,
  event: GameLaunchEvent
): boolean => event.payload.id === 'searchResult';

const currentlyOnNavigationItemPage = (context: LogicContext): boolean => {
  return (
    context.navigationLinks &&
    context.navigationLinks.some(link => link === context.currentPagePath)
  );
};

const areDifferentSegments = (
  context: LogicContext,
  event: SegmentsEvent
): boolean => {
  if (!context.segments) return true;
  const { segments } = event.data;
  return context.segments && Array.isArray(segments) && segments.length
    ? context.segments.sort().join(',') !== segments.sort().join(',')
    : false;
};

const isResponseStatusInvalid = (
  context: LogicContext,
  event: { data: { status: number } }
): boolean => {
  return event.data.status !== 200;
};

const isResponseInvalid_InternalAuthTokenVariant = (
  context: LogicContext,
  event: { data: { status: number } }
): boolean =>
  isResponseStatusInvalid(context, event) && isInternalAuthTokenVariant();

const isAuthTokenInitialSetup = (context: LogicContext): boolean => {
  return context.user.authToken?.initialSetup === true;
};

const areAnySavedSegments = (context: LogicContext): boolean => {
  return context.segments
    ? Array.isArray(context.segments) && context.segments.length > 0
    : false;
};

const isDifferentCurrency = (context: LogicContext): boolean => {
  return context.user.currency !== config.default_currency;
};

const isPhrasePendingToAddToRecentPhrases = (context: LogicContext): boolean =>
  !!context.phrasePendingAdditionToRecentPhrases;

const isSearchPhraseCorrect = (context: LogicContext): boolean => {
  const phrase = context.search.searchPhrase;

  return !!(phrase && phrase.length > 2);
};

const isGameLaunchPage = (context: LogicContext): boolean => {
  return !!context.controllersData[controllersEnum.GAME_LAUNCHER];
};

const isHeaderInitialized = (context: LogicContext) =>
  context.headerInitialized;

const isPromotionsDetailsPage = (context: LogicContext) => {
  return context.controllersData[controllersEnum.PROMOTIONS]!.components.some(
    ([id, entry]) => entry.properties.mode === 'details'
  );
};

const isClickedItemInternal = (
  context: LogicContext,
  event: { payload: { path?: string } }
) => !!event.payload.path;

const isClickedLeftSliderCommand = (
  context: LogicContext,
  event: { payload: { command?: 'leftSlider' } }
) => !!event.payload.command;

const isClickedItemAccount = (
  context: LogicContext,
  event: {
    payload: {
      account?:
        | 'deposit'
        | 'responsibleGambling'
        | 'transactionHistory'
        | 'transferToPoker';
    };
  }
) => !!event.payload.account;

const isOnLaunchPathAlready = (context: LogicContext) =>
  context.gameToLaunch?.gameLaunchPath === context.currentPagePath;

const isCookieVisible = (
  c: LogicContext,
  event: {
    data: {
      cookieBannerVisible: boolean;
    };
  }
) => {
  return event.data.cookieBannerVisible;
};

const controllersGuards = (() => {
  const map: { [key: string]: Function } = {};
  ALL_CONTROLLERS.forEach(controllerName => {
    map[`isControllerEnabled_${controllerName}`] = (
      context: LogicContext,
      {
        controllersData,
      }: {
        controllersData: ControllersData;
      }
    ) =>
      controllersData && Object.keys(controllersData).includes(controllerName);

    map[`isControllerDisabled_${controllerName}`] = (
      context: LogicContext,
      {
        controllersData,
      }: {
        controllersData: ControllersData;
      }
    ) =>
      controllersData && !Object.keys(controllersData).includes(controllerName);
  });

  return map;
})();

const isLayoutPreloaded = (context: LogicContext, event: any) =>
  event.data.isLayoutPreloaded;

const isLayoutPreloadedAndNormalPage = (
  context: LogicContext,
  event: any
): boolean => event.data.isLayoutPreloaded && !event.data.isErrorPage;

const isLayoutPreloadedAndErrorPage = (
  context: LogicContext,
  event: any
): boolean => event.data.isLayoutPreloaded && event.data.isErrorPage;

const arePersonalizedBannersAvailable = (context: LogicContext) =>
  !!context.personalizedBanners;

const isKrakenEnabled = () => config.features.header.variant === 'kraken';

const isCookieBannerEnabled = () =>
  config.staticComponents.CookieBanner?.enabled === true;

const isPersonalizedBannerInKraken = (context: LogicContext) =>
  arePersonalizedBannersAvailable(context) && isKrakenEnabled();

const isNotPersonalizedBannerInKraken = (context: LogicContext) =>
  !isPersonalizedBannerInKraken(context);

const arePromotionsEnabled = (context: LogicContext, event: any) => {
  return (
    controllersGuards[`isControllerEnabled_${controllersEnum.PROMOTIONS}`](
      context,
      event
    ) &&
    !isIframeWrapperEnabled() &&
    config.features.header.variant === 'kraken'
  );
};

const isAppInitialized = (context: LogicContext) => context.appInitialized;

// in this variant, the auth token is provided by the Data Gateway API
const isAppInitilaized_InternalTokenVariant = (context: LogicContext) => {
  return isAppInitialized(context) && isInternalAuthTokenVariant();
};

// in this variant, the auth token is provided by the MTT message bus
const isAppInitilaized_ExternalTokenVariant = (context: LogicContext) => {
  return isAppInitialized(context) && isExternalAuthTokenVariant();
};

const hasUrlRedirection = () =>
  config.urlRedirection && config.urlRedirection.length;

const shouldRedirectUrl = () => isRoutingEnabled() && hasUrlRedirection();

const guards = {
  isPersonalizedBannerInKraken,
  isNotPersonalizedBannerInKraken,
  isKrakenEnabled,
  isLayoutPreloaded,
  isLayoutPreloadedAndNormalPage,
  isLayoutPreloadedAndErrorPage,
  isAuthTokenExpired,
  requiresScrollRestore,
  isLargeScreenWithHeader,
  isCookieBannerEnabled,
  isCookieVisible,
  isHeaderInVisibleMode,
  isSessionNotResolved,
  isNotAllowedToLaunchGame,
  isNotAwaitingLogin,
  isGameDetailsPage,
  isRoutingEnabled,
  isRoutingNotEnabled,
  currentlyOnRecentlyPlayedPage,
  currentlyOnNavigationItemPage,
  isSearchPhraseCorrect,
  isGameLaunchedFromSearchCategory,
  isPhrasePendingToAddToRecentPhrases,
  isHeaderInitialized,
  isPromotionsDetailsPage,
  isClickedItemInternal,
  isClickedLeftSliderCommand,
  isClickedItemAccount,
  isOnLaunchPathAlready,
  isGameLaunchPage,
  isSidebarVisible,
  isModalVisibleOnBigScreen,
  delayedTrackingNotInstalled,
  isGameTriggeredByUserInteraction,
  areAnySavedSegments,
  areDifferentSegments,
  arePromotionsEnabled,
  isDifferentCurrency,
  isSearchWithoutGameDetailsPage,
  arePersonalizedBannersAvailable,
  isAuthTokenInitialSetup,
  isInternalAuthTokenVariant,
  isResponseStatusInvalid,
  isAppInitialized,
  isAppInitilaized_InternalTokenVariant,
  isAppInitilaized_ExternalTokenVariant,
  isResponseInvalid_InternalAuthTokenVariant,
  is404page: (_: LogicContext, event: any) => event.data.subtype === '404',
  isGameFetched: (
    _: LogicContext,
    event: { data: { game?: PortalVerticalGameTilePropTypes } }
  ) => !!event.data.game,
  shouldResetLayout: (
    context: LogicContext,
    event: { data: { shouldUpdateLayout: boolean } }
  ) => event.data.shouldUpdateLayout,
  shouldRedirectUrl,
  ...controllersGuards,
};

const guardsObject = {
  ...guards,
  every: (
    context: LogicContext,
    event: any,
    meta: { cond: { guards: string[] } }
  ): boolean => {
    return meta.cond.guards.every(cond => {
      type guardType = (
        context?: LogicContext,
        event?: EventObject | any,
        meta?: MetaObject | any
      ) => boolean;
      const guard = guards[cond as keyof typeof guards] as guardType;
      return guard(context, event, meta);
    });
  },
};

export default guardsObject;
